// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
  Stack,
} from '@mui/material';
import { ReactComponent as CloseIcon } from '../../../../assets/Icons/lucideIcons/x.svg';
import { ReactComponent as SwapVertIcon } from '../../../../assets/Icons/lucideIcons/arrow-up-down.svg';
import isArray from 'lodash/isArray';
import debounce from 'lodash/debounce';

import { momentTz } from '../../../../utils/dates';
import SearchTextField from '../../SearchTextField';
import NoDataPresentComponent from '../../NoDataPresentComponent';
import useLog from '../../../../hooks/ModelInventory/useLogs';
import { isDateValid, isValueEmpty } from '../../../../utils/Utils';
import {
  DATE_FORMAT_HH_MM_SS,
  EMPTY_ARRAY,
} from '../../../../const/CommonConst';

const DRAWER_WIDTH = 480;

const SORT_ORDER = {
  NEWEST: 'newest',
  OLDEST: 'oldest',
};

const ATTRIBUTE_LOG_TYPE = 'Attribute';
const PARAGRAPH_DATA_TYPE = 'Paragraph';
const TEMPLATE_LIST_DATA_TYPE = 'TemplateList';
const TEMPLATE_LIST_HIDDEN_KEYS = new Set([
  'template_id',
  'template_version_id',
]);

const getDisplayObjectEntries = (value, extraData) => {
  const entries = Object.entries(value || {});

  if (extraData?.attribute_data_type !== TEMPLATE_LIST_DATA_TYPE) {
    return entries;
  }

  return entries?.filter(([key]) => !TEMPLATE_LIST_HIDDEN_KEYS?.has(key));
};

const buildBulletSx = (isFirst) => (theme) => ({
  position: 'relative',
  width: 18,
  height: 18,
  borderRadius: '50%',
  backgroundColor: isFirst
    ? theme?.palette?.historyTimeLine?.activeDot
    : theme?.palette?.historyTimeLine?.inactiveDot,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  '&::after': {
    content: '""',
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: theme?.palette?.background?.paper,
    position: 'absolute',
  },
});

const connectorSx = (theme) => ({
  flex: 1,
  width: '2px',
  minHeight: 24,
  backgroundColor: theme?.palette?.historyTimeLine?.line,
});

//  Paragraph-shaped values look like `{ title, description }`.
const isParagraphValue = (value, extraData) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  if (extraData?.attribute_data_type === PARAGRAPH_DATA_TYPE) {
    return true;
  }
  const keys = Object.keys(value);
  return (
    keys?.length > 0 &&
    keys.every((k) => k === 'title' || k === 'description') &&
    (typeof value.title === 'string' || typeof value.description === 'string')
  );
};

const formatPrimitive = (val) => {
  if (val === null || val === undefined || val === '') {
    return '-';
  }
  if (typeof val === 'string' && isDateValid(val)) {
    return momentTz(val).format(DATE_FORMAT_HH_MM_SS);
  }
  if (typeof val === 'boolean' || typeof val === 'number') {
    return String(val);
  }
  return val;
};

const valueToSearchText = (value, extraData) => {
  if (isValueEmpty(value)) {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  if (isArray(value)) {
    return value?.map((v) => v?.value ?? v).join(', ');
  }
  if (isParagraphValue(value, extraData)) {
    return [value?.title, value?.description].filter(Boolean).join(' — ');
  }
  if (typeof value === 'object') {
    return getDisplayObjectEntries(value, extraData)
      .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`)
      .join(', ');
  }
  return String(value);
};

/**
 * Renders paragraph-shaped `{ title, description }` objects as a header + body.
 */
const ParagraphValue = ({ value }) => {
  if (!value?.title && !value?.description) {
    return (
      <Typography
        variant="subtitle2"
        sx={{
          color: 'text.secondary',
        }}
      >
        -
      </Typography>
    );
  }
  return (
    <Box>
      {value?.title ? (
        <Typography variant="subtitle2">{value.title}</Typography>
      ) : null}
      {value?.description ? (
        <Tooltip
          title={value.description || ''}
          arrow
          placement="bottom"
          slotProps={{
            tooltip: {
              sx: {
                maxWidth: 400,
                maxHeight: 200,
                overflowY: 'auto',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                textAlign: 'left',
                lineHeight: 1.5,
                display: 'block',
                p: 1.5,
              },
            },
          }}
        >
          <Typography
            variant="subtitle2"
            noWrap
            sx={{
              color: 'text.secondary',
            }}
          >
            {value.description}
          </Typography>
        </Tooltip>
      ) : null}
    </Box>
  );
};

ParagraphValue.propTypes = {
  value: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
};

/**
 * Renders a generic object as one key/value.
 */
const ObjectValue = ({ value, extraData }) => {
  const theme = useTheme();
  const entries = getDisplayObjectEntries(value, extraData);
  if (!entries.length) {
    return (
      <Typography
        variant="subtitle2"
        sx={{
          color: 'text.secondary',
        }}
      >
        -
      </Typography>
    );
  }
  return (
    <Box>
      {entries?.map(([key, val]) => {
        const displayVal =
          val && typeof val === 'object'
            ? JSON.stringify(val)
            : formatPrimitive(val);
        return (
          <Typography
            key={key}
            variant="subtitle2"
            noWrap
            title={displayVal}
            sx={{
              wordBreak: 'break-word',
              color: theme?.palette?.historyTimeLine?.objecetVal,
            }}
          >
            <Typography
              component="span"
              variant="subtitle2"
              sx={{ color: theme?.palette?.historyTimeLine?.objectKey }}
            >
              {key} :{' '}
            </Typography>
            {displayVal}
          </Typography>
        );
      })}
    </Box>
  );
};

ObjectValue.propTypes = {
  value: PropTypes.shape({}).isRequired,
  extraData: PropTypes.shape({
    attribute_data_type: PropTypes.string,
  }),
};

const LogValue = ({ value, extraData }) => {
  if (isValueEmpty(value)) {
    return (
      <Typography
        variant="subtitle2"
        sx={{
          color: 'text.secondary',
        }}
      >
        -
      </Typography>
    );
  }

  if (isArray(value)) {
    const text = value?.map((v) => v?.value ?? v).join(', ');
    return <Typography variant="subtitle2">{text || '-'}</Typography>;
  }

  if (typeof value === 'object') {
    if (isParagraphValue(value, extraData)) {
      return <ParagraphValue value={value} />;
    }
    return <ObjectValue value={value} extraData={extraData} />;
  }

  return (
    <Typography variant="subtitle2" sx={{ wordBreak: 'break-word' }}>
      {formatPrimitive(value)}
    </Typography>
  );
};

LogValue.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
        PropTypes.shape({
          value: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
            PropTypes.bool,
          ]),
        }),
        PropTypes.shape({}),
      ])
    ),
    PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
    }),
    PropTypes.shape({}),
  ]),
  extraData: PropTypes.shape({
    attribute_data_type: PropTypes.string,
    attribute_label: PropTypes.string,
    attribute_name: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
};

const HistoryTimelineItem = ({ entry, isFirst, isLast }) => {
  const theme = useTheme();

  const bulletSx = useMemo(() => buildBulletSx(isFirst), [isFirst]);
  return (
    <Grid container spacing={1.5} sx={{ position: 'relative' }} wrap="nowrap">
      <Grid
        sx={{
          width: 24,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 0.5,
        }}
      >
        <Box sx={bulletSx} />
        {!isLast && <Box sx={connectorSx} />}
      </Grid>
      <Grid sx={{ pb: 2.5, minWidth: 0 }} size="grow">
        <Grid
          container
          spacing={1}
          wrap="nowrap"
          sx={{
            alignItems: 'flex-start',
          }}
        >
          <Grid sx={{ minWidth: 0 }} size="grow">
            <LogValue
              value={entry?.current_value}
              extraData={entry?.extra_data}
            />
            <Typography
              variant="body2"
              color={theme?.palette?.historyTimeLine?.updatedText}
              sx={{ display: 'block', mt: 0.25 }}
            >
              Updated by {entry?.responsible_user || 'Unknown'}
            </Typography>
          </Grid>
          <Grid sx={{ flexShrink: 0, textAlign: 'right' }}>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              {entry?.modification_date
                ? momentTz(new Date(entry.modification_date)).format(
                    DATE_FORMAT_HH_MM_SS
                  )
                : '-'}
            </Typography>
            <Typography
              variant="body2"
              color={theme?.palette?.historyTimeLine?.updatedText}
              sx={{ display: 'block', mt: 0.25, whiteSpace: 'nowrap' }}
            >
              Status: {entry?.state || '-'}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

HistoryTimelineItem.propTypes = {
  entry: PropTypes.shape({
    state: PropTypes.string,
    responsible_user: PropTypes.string,
    modification_date: PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types
    current_value: PropTypes.any,
    extra_data: PropTypes.shape({}),
  }).isRequired,
  isFirst: PropTypes.bool.isRequired,
  isLast: PropTypes.bool.isRequired,
};

const getAttributeDisplayName = (attribute) => {
  const label = attribute?.label;
  const name = attribute?.name;
  const id = attribute?.id;

  if (label && String(label) !== String(id)) {
    return label;
  }
  if (name && String(name) !== String(id)) {
    return name;
  }

  return label || name || '';
};

const HistoryDrawer = ({
  open,
  handleClose,
  attribute,
  entityId,
  entityType,
}) => {
  const { getHistory } = useLog();

  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState(EMPTY_ARRAY);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState(SORT_ORDER.NEWEST);

  const attributeDisplayName = useMemo(
    () => getAttributeDisplayName(attribute),
    [attribute]
  );

  const attributeFilter = useMemo(() => {
    if (!attribute?.name) {
      return null;
    }
    return [
      {
        type: ATTRIBUTE_LOG_TYPE,
        attribute_name: attribute?.name,
        attribute_label: attribute?.label,
        attribute_id: attribute?.id,
      },
    ];
  }, [attribute?.name, attribute?.label, attribute?.id]);

  useEffect(() => {
    if (!open) {
      setHistory(EMPTY_ARRAY);
      setSearchTerm('');
      setSortOrder(SORT_ORDER.NEWEST);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !entityId || !entityType || !attributeFilter) {
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    getHistory({
      entityType,
      entityId,
      attributes: attributeFilter,
      globalSearch: false,
      text: '',
      logActions: ['Attribute'],
      state: [],
      users: [],
      dateRange: null,
    })
      .then(
        (res) => !cancelled && setHistory(res?.data?.history || EMPTY_ARRAY)
      )
      .catch(() => !cancelled && setHistory(EMPTY_ARRAY))
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [open, entityId, entityType, attributeFilter]);

  const filteredHistory = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const base = term
      ? history.filter((entry) => {
          const userText = entry?.responsible_user?.toLowerCase() || '';
          const valueText = valueToSearchText(
            entry?.current_value,
            entry?.extra_data
          )
            .toString()
            .toLowerCase();
          return userText.includes(term) || valueText.includes(term);
        })
      : history;

    const sorted = [...base].sort((a, b) => {
      const aTime = a?.modification_date
        ? new Date(a.modification_date).getTime()
        : 0;
      const bTime = b?.modification_date
        ? new Date(b.modification_date).getTime()
        : 0;
      return sortOrder === SORT_ORDER.NEWEST ? bTime - aTime : aTime - bTime;
    });

    return sorted;
  }, [history, searchTerm, sortOrder]);

  // The "Current Value" header always reflects the latest entry
  const latestEntry = useMemo(() => {
    if (!history?.length) {
      return null;
    }
    return [...history].sort((a, b) => {
      const aTime = a?.modification_date
        ? new Date(a.modification_date).getTime()
        : 0;
      const bTime = b?.modification_date
        ? new Date(b.modification_date).getTime()
        : 0;
      return bTime - aTime;
    })[0];
  }, [history]);

  const currentValueText = useMemo(() => {
    if (!latestEntry) {
      return '-';
    }
    const text = valueToSearchText(
      latestEntry?.current_value,
      latestEntry?.extra_data
    );
    return text || '-';
  }, [latestEntry]);

  const handleSearchChangeImmediate = useCallback(
    (event) => {
      setSearchTerm(event?.target?.value || '');
    },
    [setSearchTerm]
  );

  const debouncedSearchChange = useMemo(
    () => debounce(handleSearchChangeImmediate, 250),
    [handleSearchChangeImmediate]
  );

  useEffect(() => {
    return () => {
      debouncedSearchChange.cancel();
    };
  }, [debouncedSearchChange]);

  const handleToggleSort = useCallback(() => {
    setSortOrder((prev) =>
      prev === SORT_ORDER.NEWEST ? SORT_ORDER.OLDEST : SORT_ORDER.NEWEST
    );
  }, [setSortOrder]);

  const changesCount = useMemo(() => {
    return history?.length || 0;
  }, [history]);

  const sortLabel = useMemo(() => {
    return sortOrder === SORT_ORDER.NEWEST ? 'Newest first' : 'Oldest first';
  }, [sortOrder]);

  return (
    <Drawer
      variant="temporary"
      anchor="right"
      open={open}
      onClose={handleClose}
      ModalProps={{ keepMounted: false }}
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 1,
      }}
    >
      <Stack
        container
        sx={{
          width: DRAWER_WIDTH,
          height: '100%',
          flexWrap: 'nowrap',
        }}
      >
        <Grid
          container
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
          }}
        >
          <Typography variant="h5">
            History{attributeDisplayName ? `: ${attributeDisplayName}` : ''}
          </Typography>
          <IconButton
            size="small"
            onClick={handleClose}
            aria-label="Close history drawer"
          >
            <CloseIcon width="18" height="18" />
          </IconButton>
        </Grid>
        <Divider />

        <Grid
          container
          spacing={1}
          flexWrap="nowrap"
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            backgroundColor: (theme) => theme?.palette?.background?.default,
          }}
        >
          <Grid sx={{ minWidth: 0 }} size="grow">
            <Grid
              container
              spacing={1}
              sx={{
                alignItems: 'center',
                flexWrap: 'nowrap',
              }}
            >
              <Grid>
                <Typography
                  variant="body8"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  Current Value
                </Typography>
              </Grid>
              <Grid sx={{ minWidth: 0 }} size="grow">
                <Typography
                  variant="subtitle2"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={currentValueText || ''}
                >
                  {currentValueText}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid sx={{ flexShrink: 0 }}>
            <Typography
              variant="body8"
              sx={{
                color: 'text.secondary',
              }}
            >
              {changesCount} {changesCount === 1 ? 'change' : 'changes'}
            </Typography>
          </Grid>
        </Grid>

        <Grid
          container
          spacing={1}
          sx={{
            alignItems: 'center',
            paddingX: 2,
            paddingBottom: 1.5,
            flexWrap: 'nowrap',
            backgroundColor: (theme) => theme?.palette?.background?.default,
          }}
        >
          <Grid size="grow">
            <SearchTextField
              width="100%"
              onChange={debouncedSearchChange}
              placeholder="Search by users, values"
              aria-label="Search history"
            />
          </Grid>
          <Grid>
            <Button
              variant="outlined"
              size="small"
              startIcon={<SwapVertIcon width="18" height="18" />}
              onClick={handleToggleSort}
            >
              {sortLabel}
            </Button>
          </Grid>
        </Grid>
        <Divider />

        {/* Timeline  */}
        <Box
          sx={{
            flex: '1 1 auto',
            overflow: 'auto',
            paddingX: 2,
            paddingY: 2,
            backgroundColor: (theme) => theme?.palette?.background?.default,
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 6,
              }}
            >
              <CircularProgress size={28} />
            </Box>
          ) : null}

          {!loading && filteredHistory?.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                paddingY: 6,
                backgroundColor: (theme) => theme?.palette?.background?.default,
              }}
            >
              <NoDataPresentComponent message="No history available." />
            </Box>
          ) : null}

          {!loading && filteredHistory?.length > 0
            ? filteredHistory?.map((entry, index) => (
                <HistoryTimelineItem
                  key={`${entry?.modification_date || 'row'}-${entry?.responsible_user || 'unknown'}-${entry?.id ?? entry?.field ?? ''}`}
                  entry={entry}
                  isFirst={index === 0}
                  isLast={index === filteredHistory?.length - 1}
                />
              ))
            : null}
        </Box>
      </Stack>
    </Drawer>
  );
};

HistoryDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  attribute: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    label: PropTypes.string,
  }),
  entityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  entityType: PropTypes.string,
};

export default HistoryDrawer;
